import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { comboService, Combo, CreateComboRequest, UpdateComboRequest } from '../services/comboService';
import { itemService, type Item } from '../services';
import noImage from '../assets/no-image.png';
import { useToast } from '../hooks/useToast';

interface ComboFormState {
  nome: string;
  descricao: string;
  precoPromocional: string;
  ativo: boolean;
}

const emptyForm: ComboFormState = {
  nome: '',
  descricao: '',
  precoPromocional: '',
  ativo: true,
};

const CombosManagement: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [combos, setCombos] = useState<Combo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState<Combo | null>(null);
  const [form, setForm] = useState<ComboFormState>(emptyForm);
  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Record<string, { quantidade: number; obrigatorio: boolean }>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [comboToDelete, setComboToDelete] = useState<Combo | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const carregarCombos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await comboService.listCombos();
      setCombos(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar combos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCombos();
  }, []);

  useEffect(() => {
    const carregarItens = async () => {
      try {
        const items = await itemService.listItems();
        setAvailableItems(items.filter((item) => item.available));
      } catch (err) {
        console.error('Erro ao carregar itens para combos:', err);
      }
    };

    carregarItens();
  }, []);

  const handleOpenDialogForCreate = () => {
    setEditingCombo(null);
    setForm(emptyForm);
    setSelectedItems({});
    setImageFile(null);
    setIsDialogOpen(true);
  };

  const handleOpenDialogForEdit = (combo: Combo) => {
    setEditingCombo(combo);
    setForm({
      nome: combo.nome,
      descricao: combo.descricao || '',
      precoPromocional: combo.precoPromocional ? combo.precoPromocional.toString() : '',
      ativo: combo.ativo,
    });
    // Ainda não suportamos edição da composição do combo; limpar seleção para evitar inconsistências
    setSelectedItems({});
    setImageFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleChangeField = (field: keyof ComboFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleItem = (item: Item) => {
    setSelectedItems((prev) => {
      const exists = prev[item.id];
      if (exists) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [item.id]: { quantidade: 1, obrigatorio: true },
      };
    });
  };

  const handleChangeItemQuantidade = (itemId: string, quantidade: number) => {
    if (quantidade <= 0 || Number.isNaN(quantidade)) return;
    setSelectedItems((prev) =>
      prev[itemId]
        ? {
            ...prev,
            [itemId]: { ...prev[itemId], quantidade },
          }
        : prev
    );
  };

  const handleToggleItemObrigatorio = (itemId: string, obrigatorio: boolean) => {
    setSelectedItems((prev) =>
      prev[itemId]
        ? {
            ...prev,
            [itemId]: { ...prev[itemId], obrigatorio },
          }
        : prev
    );
  };

  const precoBaseCalculado = useMemo(() => {
    return Object.entries(selectedItems).reduce((total, [itemId, config]) => {
      const item = availableItems.find((i) => i.id === itemId);
      if (!item) return total;
      return total + item.price * config.quantidade;
    }, 0);
  }, [selectedItems, availableItems]);

  const handleSave = async () => {
    if (Object.keys(selectedItems).length === 0) {
      setError('Selecione ao menos um item para o combo.');
      return;
    }

    const itens = Object.entries(selectedItems).map(([itemId, config]) => {
      const item = availableItems.find((i) => i.id === itemId);
      return {
        itemId,
        nomeItemSnapshot: item?.name ?? '',
        quantidade: config.quantidade,
        obrigatorio: config.obrigatorio,
        precoUnitarioSnapshot: item?.price,
      };
    });

    const precoPromocionalNumber = form.precoPromocional
      ? Number(form.precoPromocional.replace(',', '.'))
      : undefined;

    const payload: CreateComboRequest | UpdateComboRequest = {
      nome: form.nome,
      descricao: form.descricao || undefined,
      precoBase: Number(precoBaseCalculado.toFixed(2)),
      precoPromocional: precoPromocionalNumber,
      ativo: form.ativo,
      itens,
      categoria: 'combos',
    };

    try {
      setLoading(true);
      setError(null);

      if (editingCombo) {
        const updated = await comboService.updateCombo(editingCombo.id, payload);

        if (imageFile) {
          try {
            await comboService.uploadComboImage(updated.id, imageFile);
          } catch (uploadErr: any) {
            console.error('Erro ao enviar imagem do combo:', uploadErr);
            showError(uploadErr.message || 'Erro ao enviar imagem do combo');
          }
        }

        setCombos((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const created = await comboService.createCombo(payload as CreateComboRequest);

        if (imageFile) {
          try {
            await comboService.uploadComboImage(created.id, imageFile);
          } catch (uploadErr: any) {
            console.error('Erro ao enviar imagem do combo:', uploadErr);
            showError(uploadErr.message || 'Erro ao enviar imagem do combo');
          }
        }

        setCombos((prev) => [...prev, created]);
      }

      setIsDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar combo');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (combo: Combo) => {
    setComboToDelete(combo);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setComboToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!comboToDelete) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await comboService.deleteCombo(comboToDelete.id);
      setCombos((prev) => prev.filter((c) => c.id !== comboToDelete.id));
      handleCloseDeleteDialog();
      showSuccess(`Combo "${comboToDelete.nome}" excluído com sucesso.`);
    } catch (err: any) {
      const message = err.message || 'Erro ao excluir combo';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Combos</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialogForCreate}
        >
          Novo Combo
        </Button>
      </Box>

      {error && (
        <Typography color="error" variant="body2" mb={2}>
          {error}
        </Typography>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Imagem</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Preço Base</TableCell>
            <TableCell>Preço Promocional</TableCell>
            <TableCell>Ativo</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {combos.map((combo) => (
            <TableRow key={combo.id} hover>
              <TableCell>
                <Box
                  component="img"
                  src={combo.imageUrl || noImage}
                  alt={combo.imageUrl ? combo.nome : 'Sem imagem'}
                  sx={{
                    width: 56,
                    height: 40,
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    bgcolor: 'grey.50',
                  }}
                />
              </TableCell>
              <TableCell>{combo.nome}</TableCell>
              <TableCell>{combo.descricao}</TableCell>
              <TableCell>R$ {combo.precoBase.toFixed(2)}</TableCell>
              <TableCell>
                {combo.precoPromocional ? `R$ ${combo.precoPromocional.toFixed(2)}` : '-'}
              </TableCell>
              <TableCell>{combo.ativo ? 'Sim' : 'Não'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleOpenDialogForEdit(combo)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(combo)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {combos.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                Nenhum combo cadastrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingCombo ? 'Editar Combo' : 'Novo Combo'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <Box
              sx={{
                width: '100%',
                height: 160,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={editingCombo?.imageUrl || noImage}
                alt={editingCombo?.imageUrl ? editingCombo.nome : 'Pré-visualização do combo'}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Box>
              <Button variant="outlined" component="label" disabled={loading}>
                Selecionar imagem
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setImageFile(file);
                  }}
                />
              </Button>
              {imageFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Imagem selecionada: {imageFile.name}
                </Typography>
              )}
            </Box>
            <TextField
              label="Nome"
              value={form.nome}
              onChange={(e) => handleChangeField('nome', e.target.value)}
              fullWidth
            />
            <TextField
              label="Descrição"
              value={form.descricao}
              onChange={(e) => handleChangeField('descricao', e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Preço Base (R$)"
                type="text"
                value={precoBaseCalculado.toFixed(2).replace('.', ',')}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Soma automática dos itens selecionados"
              />
              <TextField
                label="Preço Promocional (R$)"
                type="text"
                value={form.precoPromocional}
                onChange={(e) => handleChangeField('precoPromocional', e.target.value)}
                fullWidth
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ativo}
                  onChange={(e) => handleChangeField('ativo', e.target.checked)}
                  color="primary"
                />
              }
              label="Ativo"
            />
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                Itens do combo
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selecione os itens que compõem este combo, ajuste as quantidades e marque se são obrigatórios.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Incluir</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Preço</TableCell>
                    <TableCell>Qtd.</TableCell>
                    <TableCell>Obrigatório</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableItems.map((item) => {
                    const selected = selectedItems[item.id];
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Switch
                            checked={!!selected}
                            onChange={() => handleToggleItem(item)}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>
                          R$ {item.price.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={selected?.quantidade ?? ''}
                            onChange={(e) => handleChangeItemQuantidade(item.id, Number(e.target.value))}
                            disabled={!selected}
                            inputProps={{ min: 1, style: { width: 70 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={selected?.obrigatorio ?? true}
                            onChange={(e) => handleToggleItemObrigatorio(item.id, e.target.checked)}
                            color="primary"
                            size="small"
                            disabled={!selected}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          Deseja realmente excluir o combo "{comboToDelete?.nome}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={loading}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CombosManagement;
